// Auth context — now Firebase-backed (was Supabase). Keeps the SAME interface
// and export names (`useAuth`, `AuthProvider`) so the ~15 consumers compile
// unchanged during the migration. Reads the caller's role + clientId from their
// Firestore `profiles/{uid}` doc.
import { createContext, useContext, useEffect, useState, type ReactNode, useRef } from "react";
import {
  onAuthStateChanged, signInWithEmailAndPassword, createUserWithEmailAndPassword,
  updateProfile, signOut as fbSignOut, type User,
} from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { firebaseAuth, firestore } from "@/integrations/firebase/client";

type Role = "admin" | "client";

type AuthCtx = {
  user: User | null;
  loading: boolean;
  role: Role | null;
  clientId: string | null;
  clientName: string | null;
  /** The profile couldn't be read (network/permissions). Not the same as "no dealership yet". */
  profileError: string | null;
  adminClientFilter: string | null;
  setAdminClientFilter: (id: string | null) => void;
  effectiveClientId: string | null;
  refresh: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, fullName: string) => Promise<void>;
  signOut: () => Promise<void>;
};

const Ctx = createContext<AuthCtx | null>(null);
const FILTER_KEY = "adminClientFilter";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState<Role | null>(null);
  const [clientId, setClientId] = useState<string | null>(null);
  const [clientName, setClientName] = useState<string | null>(null);
  // Set when the profile couldn't be read at all — distinct from "no dealership yet".
  const [profileError, setProfileError] = useState<string | null>(null);
  // Only the most recent profile load may write state (sign-in events can overlap).
  const loadSeq = useRef(0);
  // Admin's selected dealership survives a page refresh (per browser tab).
  const [adminClientFilter, setAdminClientFilterState] = useState<string | null>(() => {
    try { return typeof window !== "undefined" ? sessionStorage.getItem(FILTER_KEY) : null; } catch { return null; }
  });
  const setAdminClientFilter = (id: string | null) => {
    setAdminClientFilterState(id);
    try { if (id) sessionStorage.setItem(FILTER_KEY, id); else sessionStorage.removeItem(FILTER_KEY); } catch { /* storage blocked */ }
  };

  const loadProfile = async (u: User | null) => {
    const seq = ++loadSeq.current;
    const current = () => seq === loadSeq.current;
    if (!u) {
      setRole(null); setClientId(null); setClientName(null); setProfileError(null); setLoading(false);
      return;
    }
    // Right after sign-in the first read can be refused or slow while the new
    // token propagates; retry briefly before giving up.
    let lastError: unknown = null;
    for (let attempt = 0; attempt < 4; attempt++) {
      if (!current()) return;
      try {
        const ref = doc(firestore(), "profiles", u.uid);
        const prof = await getDoc(ref);
        // An account made outside sign-up (e.g. in the Firebase console) has no
        // profile, so admins couldn't see it to give it access. Create it as an
        // unassigned client (all the rules allow) so it shows up in Admin.
        if (!prof.exists()) {
          await setDoc(ref, { id: u.uid, email: u.email ?? null, full_name: u.displayName ?? null, role: "client", clientId: null });
        }
        const data = prof.data() as { role?: Role; clientId?: string | null } | undefined;
        let name: string | null = null;
        if (data?.clientId) {
          const c = await getDoc(doc(firestore(), "clients", data.clientId));
          name = (c.data() as { name?: string } | undefined)?.name ?? null;
        }
        if (!current()) return;
        setRole(data?.role ?? "client");
        setClientId(data?.clientId ?? null);
        setClientName(name);
        setProfileError(null);
        setLoading(false);
        return;
      } catch (e) {
        lastError = e;
        await new Promise((r) => setTimeout(r, 400 * 2 ** attempt));
      }
    }
    if (!current()) return;
    console.warn("[Auth] Failed to load profile:", lastError);
    // Never guess a role: a failed read must not look like "no dealership assigned".
    setRole(null); setClientId(null); setClientName(null);
    setProfileError(lastError instanceof Error ? lastError.message : String(lastError));
    setLoading(false);
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(firebaseAuth(), (u) => {
      setUser(u);
      setLoading(true);
      void loadProfile(u);
    });
    return () => unsub();
  }, []);

  const effectiveClientId = role === "admin" ? adminClientFilter : clientId;

  return (
    <Ctx.Provider
      value={{
        user,
        loading,
        role,
        clientId,
        clientName,
        profileError,
        adminClientFilter,
        setAdminClientFilter,
        effectiveClientId,
        refresh: () => { setLoading(true); return loadProfile(user); },
        signIn: async (email, password) => {
          await signInWithEmailAndPassword(firebaseAuth(), email, password);
        },
        signUp: async (email, password, fullName) => {
          const cred = await createUserWithEmailAndPassword(firebaseAuth(), email, password);
          if (fullName) await updateProfile(cred.user, { displayName: fullName });
          // Self-created profile: unassigned client (rules forbid anything else).
          // It then shows in Admin → Users so an admin can link a dealership,
          // matching the Supabase handle_new_user trigger.
          await setDoc(doc(firestore(), "profiles", cred.user.uid), {
            id: cred.user.uid, email, full_name: fullName || null, role: "client", clientId: null,
          });
          await loadProfile(cred.user);
        },
        signOut: async () => { await fbSignOut(firebaseAuth()); },
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function useAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth outside provider");
  return v;
}
